/**
 * Session handling in the browser.
 *
 * The access token lives only in memory. The long-lived refresh credential is
 * an HttpOnly cookie the page can't read; `refresh()` trades it for a fresh
 * access token. Everything goes through same-origin `/api/*`.
 */
import type { ApiUser, AuthResponse } from "@wreps/shared";

const LAST_USER_KEY = "wreps:lastUser";
const CLIENT_HEADERS = { "X-Wreps-Client": "web" };

export type RefreshResult = "ok" | "signed_out" | "offline";

export class ApiHttpError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`API request failed with status ${status}`);
    this.name = "ApiHttpError";
  }
}

/** Thrown when the network is unreachable (offline, DNS, proxy down). */
export class OfflineError extends Error {
  override name = "OfflineError";
}

type Listener = (user: ApiUser | null) => void;

export class Auth {
  private accessToken: string | null = null;
  private expiresAt = 0;
  private refreshing: Promise<RefreshResult> | null = null;
  private listeners = new Set<Listener>();
  user: ApiUser | null = null;

  constructor(private readonly fetchImpl: typeof fetch = (...a) => fetch(...a)) {}

  /** The last account signed in on this device, for starting up offline. */
  static lastUser(): ApiUser | null {
    try {
      const raw = localStorage.getItem(LAST_USER_KEY);
      return raw ? (JSON.parse(raw) as ApiUser) : null;
    } catch {
      return null;
    }
  }

  onChange(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private accept(body: AuthResponse): void {
    this.accessToken = body.accessToken;
    // Renew a minute early to avoid racing the expiry.
    this.expiresAt = Date.now() + (body.expiresIn - 60) * 1000;
    this.user = body.user;
    try {
      localStorage.setItem(LAST_USER_KEY, JSON.stringify(body.user));
    } catch {
      // storage unavailable (private mode); offline start just won't work
    }
    for (const fn of this.listeners) fn(this.user);
  }

  private clear(): void {
    this.accessToken = null;
    this.expiresAt = 0;
    this.user = null;
    try {
      localStorage.removeItem(LAST_USER_KEY);
    } catch {}
    for (const fn of this.listeners) fn(null);
  }

  private async post(path: string, body?: unknown): Promise<Response> {
    try {
      return await this.fetchImpl(`/api${path}`, {
        method: "POST",
        credentials: "same-origin",
        headers: { ...CLIENT_HEADERS, ...(body ? { "Content-Type": "application/json" } : {}) },
        body: body ? JSON.stringify(body) : null,
      });
    } catch {
      throw new OfflineError("network unavailable");
    }
  }

  /** Exchange the refresh cookie for an access token. Concurrent calls share one request. */
  refresh(): Promise<RefreshResult> {
    this.refreshing ??= (async (): Promise<RefreshResult> => {
      try {
        const res = await this.post("/v1/auth/refresh");
        if (res.ok) {
          this.accept((await res.json()) as AuthResponse);
          return "ok";
        }
        if (res.status === 401 || res.status === 403) {
          this.clear();
          return "signed_out";
        }
        return "offline"; // 5xx / proxy trouble: treat like a network blip
      } catch {
        return "offline";
      } finally {
        this.refreshing = null;
      }
    })();
    return this.refreshing;
  }

  async signInWithGoogle(credential: string): Promise<ApiUser> {
    return this.signIn("/v1/auth/google", { credential });
  }

  async devSignIn(email: string): Promise<ApiUser> {
    return this.signIn("/v1/auth/dev", { email });
  }

  private async signIn(path: string, body: unknown): Promise<ApiUser> {
    const res = await this.post(path, body);
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(err.error ?? `Sign-in failed (${res.status})`);
    }
    const auth = (await res.json()) as AuthResponse;
    this.accept(auth);
    return auth.user;
  }

  async signOut(): Promise<void> {
    try {
      await this.post("/v1/auth/logout");
    } catch {
      // Offline: the cookie stays valid server-side until it expires, but this
      // device forgets the user either way.
    }
    this.clear();
  }

  async deleteAccount(): Promise<void> {
    await this.fetchJson("/v1/me", { method: "DELETE" });
    this.clear();
  }

  /**
   * Authenticated JSON request. Refreshes the access token when needed and
   * retries once on 401. Throws OfflineError, or ApiHttpError for non-2xx.
   */
  async fetchJson<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
    const attempt = async (): Promise<Response> => {
      if (!this.accessToken || Date.now() >= this.expiresAt) {
        const r = await this.refresh();
        if (r === "offline") throw new OfflineError("cannot refresh session");
        if (r === "signed_out") throw new ApiHttpError(401, { error: "signed out" });
      }
      const headers = new Headers(init.headers);
      headers.set("Authorization", `Bearer ${this.accessToken}`);
      let body = init.body;
      if (init.json !== undefined) {
        headers.set("Content-Type", "application/json");
        body = JSON.stringify(init.json);
      }
      try {
        return await this.fetchImpl(`/api${path}`, {
          ...init,
          headers,
          body: body ?? null,
          credentials: "same-origin",
        });
      } catch {
        throw new OfflineError("network unavailable");
      }
    };

    let res = await attempt();
    if (res.status === 401) {
      this.accessToken = null; // force a refresh, then one retry
      res = await attempt();
    }
    const data = (await res.json().catch(() => null)) as unknown;
    if (!res.ok) throw new ApiHttpError(res.status, data);
    return data as T;
  }
}
